import { Col } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { flushBuffer, trackStructEvent } from "@snowplow/browser-tracker";
import { useContext, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { BLOCKING_ERROR_TYPE } from "@/apis/constants";
import { Logger } from "@/apis/Logger";
import AllStepsCompleted from "@/components/AllStepsCompleted";
import AmazonAccountCaching from "@/components/AmazonAccountCaching";
import AmazonPassword from "@/components/AmazonPassword";
import AmazonUsername from "@/components/AmazonUsername";
import ErrorBanner from "@/components/Commons/ErrorBanner";
import { AppNotAvailableError } from "@/components/Commons/ErrorBanner/AppNotAvailableError";
import { IneligibleError } from "@/components/Commons/ErrorBanner/IneligibleError";
import StepContainer from "@/components/Commons/StepContainer";
import ConnectVpn from "@/components/ConnectVpn";
import { DAY1_ONBOARDING_STEPS, STEP_ID } from "@/components/constants";
import { ChatWithITSupportFlyoutLink } from "@/components/GetSupport";
import { getRefPageByError } from "@/components/GetSupport/ChatWidget/helper";
import IdentityCheck from "@/components/IdentityCheck";
import InsertAndTestSecurityKey from "@/components/InsertAndTestSecurityKey";
import PreCacheADMobile from "@/components/PreCacheADMobile";
import RegisterSecurityKey from "@/components/RegisterSecurityKey";
import { setStepCache } from "@/helpers/CachedStepProgress";
import { SnowplowTracker } from "@/helpers/snowplow";
import { useCheckReqs } from "@/hooks/useCheckReqs";
import { useLoadRegion } from "@/hooks/useLoadRegion";
import { AppContext } from "@/stores/appStore";
import { ActionType, getEnabledSteps } from "@/stores/constants";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

export const STEP_COMPONENT_MAP: Record<STEP_ID, React.ComponentType> = {
  [STEP_ID.IDENTITY_CHECK]: IdentityCheck,
  [STEP_ID.AMAZON_USERNAME]: AmazonUsername,
  [STEP_ID.INSERT_AND_TEST_SECURITY_KEY]: InsertAndTestSecurityKey,
  [STEP_ID.REGISTER_SECURITY_KEY]: RegisterSecurityKey,
  [STEP_ID.CONNECT_VPN]: ConnectVpn,
  [STEP_ID.AMAZON_PASSWORD]: AmazonPassword,
  [STEP_ID.PRE_CACHE_AD_MOBILE]: PreCacheADMobile,
  [STEP_ID.AMAZON_ACCOUNT_CACHING]: AmazonAccountCaching,
};

export default function HomeContent(): JSX.Element {
  const intl = useIntl();

  const onboardingSteps = () => {
    return getEnabledSteps(DAY1_ONBOARDING_STEPS).map((step) => {
      if (STEP_COMPONENT_MAP[step.id]) {
        const StepContent = STEP_COMPONENT_MAP[step.id];
        return (
          <StepContainer key={step.id} stepId={step.id}>
            <StepContent></StepContent>
          </StepContainer>
        );
      }
      void Logger.error(
        `Error in loading the step content since the ${step.id} component not set`
      );
      return null;
    });
  };

  /** Identity the healthy region and init Amplify configuration */
  const { isError: isLoadRegionError } = useLoadRegion({});

  const {
    isFetched: isCheckReqsFetched,
    isError: isCheckReqsError,
    candidateLocalStartDate,
    blockingErrorType,
  } = useCheckReqs();
  const { state, dispatch } = useContext(AppContext);
  const allStepsCompleted = getEnabledSteps(state.steps).every(
    (step) => step.completed
  );

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support. When a blocking error of any kind occurs, override the
   * step-default refPage (e.g. "ezo-identity-check") with one that reflects the
   * actual screen the new hire is seeing — otherwise chat support and our logs
   * mislabel a blocking-error session as if the user were on the identity-check
   * step.
   */
  useEffect(() => {
    if (!blockingErrorType) {
      return;
    }
    if (
      blockingErrorType === BLOCKING_ERROR_TYPE.BeforeDay1 &&
      !candidateLocalStartDate
    ) {
      // BeforeDay1 without a start date can't render the dedicated banner;
      // wait until candidateLocalStartDate is set before dispatching to keep
      // existing behavior for that branch.
      return;
    }
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: getRefPageByError(blockingErrorType),
    });
  }, [blockingErrorType, candidateLocalStartDate]);

  useEffect(() => {
    if (allStepsCompleted) {
      trackStructEvent({
        category: Day1EventCategory.Complete,
        action: Day1EventAction.SectionToggled,
      });
      dispatch({
        type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
        nonDirectedChatSupportContext: "ON_WIZARD_COMPLETED_STEP",
      });
      // Force all buffered events to be flushed since this is the end of the app flow.
      // Otherwise buffered events may be lost.
      flushBuffer();
    }
  }, [allStepsCompleted]);

  // Keep the cache up to date when the wizard advances.
  useEffect(() => {
    setStepCache(state.currentStepNum - 1);
  }, [state.currentStepNum]);

  useEffect(() => {
    if (isCheckReqsFetched) {
      SnowplowTracker.trackSystemInfo();
    }
  }, [isCheckReqsFetched]);

  if (
    blockingErrorType === BLOCKING_ERROR_TYPE.BeforeDay1 &&
    candidateLocalStartDate
  ) {
    return (
      <ErrorBanner>
        <FormattedMessage
          id="error-beforeStartDate"
          values={{
            chatWithITSupportLink: (
              <ChatWithITSupportFlyoutLink
                localeMessageId="text-chatWithITSupport"
                metricProps={{
                  category: Day1EventCategory.BlockingError,
                  action: Day1EventAction.ChatWithSupport,
                  label: Day1EventLabel.NotDay1,
                }}
              />
            ),
            startDate: intl.formatDate(candidateLocalStartDate.toDate(), {
              month: "long",
              day: "numeric",
            }),
          }}
        />
      </ErrorBanner>
    );
  }

  /**
   * Failed to resolve a healthy EZO API region, so EZO is unavailable.
   * Direct the new hire to use First Aid and engage their manager to
   * onboard with virtual new hire orientation (vNHO)
   */
  if (isLoadRegionError) {
    return <AppNotAvailableError />;
  }

  if (isCheckReqsError) {
    return <IneligibleError />;
  }

  if (isCheckReqsFetched) {
    return (
      <Col gridGap="S400">
        {onboardingSteps()}
        {allStepsCompleted && <AllStepsCompleted />}
      </Col>
    );
  }

  return (
    <Spinner
      dataTestId="homeContentSpinner"
      showText
      size={SpinnerSize.Medium}
    />
  );
}
